/**
 * Light / Dark theme helpers.
 *
 * The whole app paints through `var(--color-*)` tokens defined in the
 * Tailwind v4 `@theme` block (light values) and overridden under
 * `html.dark` in index.css — so flipping the `dark` class on <html>
 * re-themes every token-driven surface at once.
 *
 * The theme PREFERENCE (Computer / Light / Dark) is an account-level setting
 * owned by `display-prefs.js`, which also applies the `dark` class and
 * follows the computer's setting live. These helpers only resolve a
 * preference to the concrete theme on screen.
 */

/** Does the OS currently prefer dark? Safe when matchMedia is missing. */
export function systemPrefersDark() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )
}

/** Resolve a preference to the concrete 'light' | 'dark' to render. */
export function resolveTheme(pref) {
  return pref === 'dark' || (pref === 'system' && systemPrefersDark())
    ? 'dark'
    : 'light'
}

/* ── Contrast-safe ink for tenant-chosen colours ──────────────────────────
 *
 * Workflow-state colours are tenant data: a company can pick any hex, and the
 * task lists paint the state NAME in that colour. The seeded "TO DO" grey
 * (#94a3b8) measured 2.38:1 on the light canvas — well under the WCAG AA 4.5:1
 * floor — and any pale colour a tenant chooses fails the same way, so fixing
 * the seed alone would not close the hole. `readableInk` keeps the hue the
 * tenant picked and moves only its lightness until it clears AA against the
 * surface it is painted on. The untouched colour is still used for the solid
 * swatch dot beside the label, where contrast rules do not apply.
 */

/** Worst-case surface per theme: the lightest dark surface (card-hover) and
 *  the darkest light surface (canvas), so a pass here passes everywhere. */
const WORST_SURFACE = { light: '#f5f7fa', dark: '#1e2128' }

function toRgb(hex) {
  const h = String(hex ?? '').trim().replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  if (!/^[0-9a-f]{6}$/i.test(full)) return null
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16))
}

function luminance([r, g, b]) {
  const lin = (c) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/** WCAG 2.1 contrast ratio between two hex colours (1 – 21). */
export function contrastRatio(a, b) {
  const ra = toRgb(a)
  const rb = toRgb(b)
  if (!ra || !rb) return 1
  const la = luminance(ra)
  const lb = luminance(rb)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * Return `hex` if it already meets `target` contrast against the current
 * theme's worst-case surface, else the nearest same-hue colour that does.
 * Unparseable input falls back to the theme's body ink token.
 *
 * @param hex      the tenant's colour
 * @param resolved 'light' | 'dark' — omit to read the live theme
 * @param target   contrast floor; 4.5 is AA for normal text
 */
export function readableInk(hex, resolved, target = 4.5) {
  const theme =
    resolved ??
    (typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')
      ? 'dark'
      : 'light')
  const bg = WORST_SURFACE[theme] ?? WORST_SURFACE.light
  const rgb = toRgb(hex)
  if (!rgb) return 'var(--color-ink)'
  if (contrastRatio(hex, bg) >= target) return hex

  // Walk toward white on a dark surface / black on a light one. Equal steps on
  // all three channels hold the hue while only lightness moves.
  const step = theme === 'dark' ? 4 : -4
  let [r, g, b] = rgb
  for (let i = 0; i < 64; i++) {
    r = Math.max(0, Math.min(255, r + step))
    g = Math.max(0, Math.min(255, g + step))
    b = Math.max(0, Math.min(255, b + step))
    const next =
      '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')
    if (contrastRatio(next, bg) >= target) return next
  }
  // Pure black / white is the most contrast available; hand back the token so
  // the label is at least painted in the theme's own readable ink.
  return 'var(--color-ink)'
}
